/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { IItem } from "../../../../interfaces/items";
import { useParams } from "react-router-dom";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import { createItem, deleteItems, listItems, showItem, updateItem } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";

const DEFAULT_DATA: IItem = {
  id: '',
  name: '',
  isActive: false,
  description: "",
  category: ""
};

export const useItems = ({ filter: filterInitial, disabledShowId, includes = [], disabledGetAll, defaultLimit }: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>,
  includes?: string[],
}) => {
  const { idItem: id } = useParams();
  const [items, setItems] = useState<IItem[]>([]);
  const [item, setItem] = useState<IItem>(DEFAULT_DATA);
  const { filter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit });
  const [totalItems, setTotalItems] = useState<number>(0);
  const { goAdd, goEdit, goBack } = useEntityNavigation({ basePath: "/towermanagement/items" });

  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleItem = (key: string, value: any, custom?: (v: any) => any) => {
    setItem((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      return { ...curr, ...newData };
    });
  };

  const deleteMany = async (ids: number[]) => {
    setLoading(true);
    return deleteItems({ ids })
      .then(async () => {
        toast.success(`Itens deletados com sucesso!`);
        await getAll({ page: 1, limit, filter });
      })
      .finally(() => setLoading(false));
  };

  const getAll = useCallback(debounce(({
    page = 1,
    limit,
    filter,
    sort
  }: {
    page?: number;
    limit?: number;
    filter?: any
    sort?: Record<string, any>
  }) => {
    setLoading(true);
    listItems({ includes: ['unitOfMeasure', ...includes], filter, page, limit, sort })
      .then(({ items: listItems, limit, currentPage, totalItems }) => {
        setItems(listItems);
        
        setLimit(Number(limit));
        setTotalItems(totalItems)
      })
      .finally(() => setLoading(false));
  }, 300), []);

  const search = useEntitySearch({ list: listItems, key: "items", includes: ["idSite", ...includes], filter });


  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showItem({ id, includes: ['unitOfMeasure'] })
      .then(({ item: ItemsShow }) => {
        setItem(ItemsShow);
      })
      .finally(() => setLoading(false));
  };

  const create = () => {
    setLoading(true);
    createItem({ item })
      .then(() => {
        toast.success(`Item criado com sucesso!`);
      })
      .finally(() => setLoading(false));
  };

  const update = () => {
    setLoading(true);
    updateItem({ id: item.id!, item })
      .then(() => {
        toast.success(`Item atualizado com sucesso!`);
      })
      .finally(() => setLoading(false));
  };


  useEffect(() => {
    if (!disabledGetAll) getAll({ page, limit, filter, sort })
  }, [page, limit, filter, sort])

  useEffect(() => {
    if (id && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
  }, []);

  return {
    items,
    item,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handleItem,
    create,
    id,
    update,
    totalItems,
    search,
    handleFilter,
    handleSort,
    deleteMany,
  };
};
